import { forwardRef, ReactNode, Ref, useState, useCallback, FocusEvent, useEffect } from 'react';

import { useTranslation } from 'react-i18next';
import { Alert, Input, TInputProps, Button, Divider } from '@treezor/design-system';

import { IPostCreateUser } from '@/features/user/contexts/CreateUserContext';
import { useAsyncInput } from '@/hooks/useAsyncInput';
import { ISirenUserData } from '@/features/user/store/user.model';

import { UserTools } from '@/utils/tools/UserTools';

import { useAppDispatch } from '@/hooks/redux-helpers';
import { useDashboardConfig } from '@/hooks/useDashboardConfig';

import { openModal } from '@/store/actions/core.actions';

import { ISelectCompanyModalProps } from './SelectCompanyModal';

type TSirenInput = Omit<TInputProps, 'type' | 'children' | 'value'> & {
  value: string;
  children: ReactNode;
  hasEntered?: boolean;
  handlePrefillData: (data: IPostCreateUser) => void;
};

export const SirenInput = forwardRef<HTMLInputElement & HTMLTextAreaElement, TSirenInput>(
  ({ value, hasEntered, label, handlePrefillData, children, ...rest }: TSirenInput, ref) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [shouldShowContent, setShow] = useState(hasEntered);
    const [inputValue, setInputValue] = useState(value);
    const [selected, setSelected] = useState<string>();

    const config = useDashboardConfig();

    const isFieldValid = value === '' || UserTools.validateIntLegalNumber(value || '');

    const handleSiren = useCallback((response) => {
      return ((response?.['businesssearchs'] as ISirenUserData[]) || []).filter((result) => result.status === 'A');
    }, []);

    const { data, iconStatus, status } = useAsyncInput<ISirenUserData>({
      key: 'siren',
      inputValue,
      url: `${config.api.baseUrl}/v1/businesssearchs?country=FR&registrationNumber=${inputValue}`,
      handleResponse: handleSiren,
      shouldSkip: !isFieldValid,
    });

    const handleAsyncBlur = useCallback(
      (event: FocusEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        rest?.onBlur?.(event as FocusEvent<HTMLTextAreaElement>);
        const formattedValue = event.target.value.replaceAll(' ', '');
        if (event.target.value !== formattedValue) {
          handlePrefillData({ legalRegistrationNumber: formattedValue });
        }
        setInputValue(formattedValue);
      },
      [rest, handlePrefillData],
    );

    const onPrefillData = useCallback(
      (prefillData: ISirenUserData) => {
        setShow(true);
        const {
          legalName,
          legalTvaNumber,
          legalRegistrationNumber,
          phone,
          city,
          country,
          address1,
          postcode,
          activityType,
        } = prefillData || {};

        handlePrefillData({
          legalRegistrationNumber,
          legalName,
          legalTvaNumber,
          phone,
          city,
          country,
          address1,
          postcode,
          legalSectorType: 'NAF',
          legalSector: activityType,
        });

        setSelected(legalRegistrationNumber);
      },
      [handlePrefillData],
    );

    const onSelectCompany = useCallback(
      () =>
        dispatch(
          openModal<ISelectCompanyModalProps>('selectCompanyModal', {
            data,
            selected,
            onConfirm: onPrefillData,
          }),
        ),
      [data, dispatch, selected, onPrefillData],
    );

    const getResultBlock = useCallback((): {
      type: 'success' | 'danger';
      title: string;
      description?: string;
      action?: {
        title: string;
        onClick: () => void;
      };
      message?: string;
    } => {
      if (status === 'success') {
        if (data?.length === 1) {
          return {
            type: 'success',
            title: t('pages.dashboard.createUser.form.siren.oneCompanyFound'),
            description: `${data[0].legalName}\n ${data[0].address1}\n ${data[0].postcode} ${data[0].city}`,
            action: {
              title: t('pages.dashboard.createUser.form.siren.prefillData'),
              onClick: () => onPrefillData(data[0]),
            },
          };
        }
        return {
          type: 'success',
          title: t('pages.dashboard.createUser.form.siren.multipleCompanyFound'),
          action: { title: t('pages.dashboard.createUser.form.siren.selectCompany'), onClick: onSelectCompany },
        };
      }

      if (status === 'error') {
        return {
          type: 'danger',
          title: t('pages.dashboard.createUser.form.siren.noCompanyFound'),
          message: t('pages.dashboard.createUser.form.siren.enterManually'),
        };
      }
    }, [t, data, status, onPrefillData, onSelectCompany]);

    const blockInfo = getResultBlock();

    useEffect(() => {
      if (status === 'error') {
        setShow(true);
      }
    }, [status]);

    return (
      <>
        <Alert
          variant="secondary"
          title={t('pages.dashboard.createUser.form.siren.alertInformation')}
          description={t('pages.dashboard.createUser.form.siren.sirenDescription')}
        />
        <Input
          type="text"
          label={label}
          icon={{
            right: iconStatus,
          }}
          error={isFieldValid ? undefined : t('pages.dashboard.createUser.form.rules.incorrectLegalNumber')}
          {...rest}
          onBlur={handleAsyncBlur}
          ref={ref as Ref<HTMLInputElement & HTMLTextAreaElement>}
        />
        {status && status !== 'pending' && (
          <Alert
            isToast={false}
            variant="secondary"
            type={blockInfo.type}
            title={blockInfo.title}
            description={blockInfo.description || blockInfo.message}
            action={blockInfo.action}
            className="whitespace-pre-line"
          />
        )}
        {shouldShowContent ? (
          children
        ) : (
          <div className="flex items-center gap-4">
            <Button variant="ghost" icon="edit_square" size="s" onClick={() => setShow(true)} className="text-nowrap">
              {t('pages.dashboard.createUser.form.actions.enterManually')}
            </Button>
            <Divider thickness="200" />
          </div>
        )}
      </>
    );
  },
);

SirenInput.displayName = 'SirenInput';
